// SessionGate — wraps every authenticated shell (see PrivateRoute in App.js).
//
// PrivateRoute decides from browser storage whether there is a session to
// try at all; this gate decides what to SHOW, from the server's answer:
//
//   identity confirmed            → the app (+ ConnectionBanner if the server
//                                   becomes unreachable later)
//   identity not known yet        → VerifyingAccountScreen (skeleton, no name)
//   server unreachable / failing  → ConnectionProblemScreen (session kept,
//                                   automatic retry, Retry / Sign out)
//   session ended by the server   → performLogout() → /login with a notice
//                                   (done by apiFetch / the identity store)
//
// The shell is never mounted for an unconfirmed identity, so no page can fall
// back to a stored or invented name.
import React, { useEffect, useState } from "react";
import { useProfile } from "../../hooks/useProfile";
import { useLogout } from "../../hooks/useLogout";
import { performLogout } from "../../context/AuthContext";
import { SESSION_END_REASONS } from "../../utils/authErrors";
import { useConnectionStatus, retryConnectionNow } from "../../utils/connectionStatus";
import VerifyingAccountScreen from "./VerifyingAccountScreen";
import ConnectionProblemScreen, { connectionProblemVariant } from "./ConnectionProblemScreen";
import ConnectionBanner from "./ConnectionBanner";

// The connection screen's "Sign out": the server is known to be unreachable,
// so don't wait the normal 8s for it to revoke the refresh token.
const SIGN_OUT_SERVER_TIMEOUT_MS = 1500;

const hasToken = () => {
  try {
    const token = localStorage.getItem("token");
    return Boolean(token) && token !== "null" && token !== "undefined";
  } catch (err) {
    return false;
  }
};

export default function SessionGate({ children }) {
  const { profile, status, failure, slow } = useProfile();
  const connection = useConnectionStatus();
  const { logout } = useLogout();
  const [signingOut, setSigningOut] = useState(false);

  // Stored session hints without an access token cannot be verified by
  // anyone — that session is over.
  useEffect(() => {
    if (!hasToken()) performLogout(SESSION_END_REASONS.ENDED);
  }, []);

  if (status === "ok" && profile) {
    return (
      <>
        {children}
        {connection.status !== "online" && (
          <ConnectionBanner
            status={connection.status}
            checking={connection.checking}
            nextRetryAt={connection.nextRetryAt}
            onRetry={retryConnectionNow}
          />
        )}
      </>
    );
  }

  if (status === "offline" || status === "unavailable") {
    const signOut = async () => {
      setSigningOut(true);
      try {
        await logout({ timeoutMs: SIGN_OUT_SERVER_TIMEOUT_MS });
      } finally {
        setSigningOut(false);
      }
    };
    return (
      <ConnectionProblemScreen
        variant={connectionProblemVariant({
          identityStatus: status,
          connectionStatus: connection.status,
          failure,
        })}
        referenceId={failure?.referenceId || connection.referenceId}
        checking={connection.checking}
        nextRetryAt={connection.nextRetryAt}
        onRetry={retryConnectionNow}
        onSignOut={signOut}
        signingOut={signingOut}
      />
    );
  }

  // idle / loading, or "invalid" while the logout redirect is under way.
  return <VerifyingAccountScreen slow={slow} />;
}
